import twemoji from "twemoji";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HtmlToPdfMakeFn = any;
type MarkedModule = typeof import("marked");

interface PdfGeneratorOptions {
  fileName?: string;
}

interface FontSourceMap {
  normal: string;
  bold: string;
  italics: string;
  bolditalics: string;
}

export interface MarkdownPdfFontOptions {
  fontFamily?: string;
  sources?: Partial<FontSourceMap>;
  vfs?: Record<string, string>;
  vfsFileNames?: Partial<FontSourceMap>;
}

interface PdfMakeFontFamily {
  normal: string;
  bold: string;
  italics: string;
  bolditalics: string;
}

interface PdfMakeLike {
  vfs?: Record<string, string>;
  fonts?: Record<string, PdfMakeFontFamily>;
  createPdf: (docDefinition: Record<string, unknown>) => {
    download: (fileName?: string, cb?: () => void) => void;
  };
}

const DEFAULT_FILE_NAME = "markdown-export.pdf";
const DEFAULT_FONT_FAMILY = "CiscoSans";
const FALLBACK_FONT_FAMILY = "Roboto";
const TWEMOJI_CDN_BASE_URL = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/";
const TWEMOJI_UNPKG_BASE_URL = "https://unpkg.com/twemoji@14.0.2/dist/assets/";
const LIGHTWEIGHT_FONT_FAMILY = FALLBACK_FONT_FAMILY;
const TWEMOJI_SVG_FOLDER = "svg";
const TWEMOJI_PNG_FOLDER = "72x72";
const EMOJI_RASTER_SIZE = 128;
const NON_STANDARD_PDF_GLYPH_REGEX =
  /[\u0100-\u024F\u02B0-\u02FF\u0300-\u036F\u0400-\u04FF\u0590-\u05FF\u0600-\u06FF\u0900-\u097F\u2000-\u206F\u20A0-\u27BF\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF\uFE10-\uFE6F\uFF00-\uFFEF]/;

const DEFAULT_FONT_SOURCES: FontSourceMap = {
  normal: "/fonts/CiscoSans-Regular.otf",
  bold: "/fonts/CiscoSans-Bold.otf",
  italics: "/fonts/CiscoSans-Italic.otf",
  bolditalics: "/fonts/CiscoSans-BoldItalic.otf",
};

let configuredFontFamily = DEFAULT_FONT_FAMILY;
let configuredFontSources: FontSourceMap = { ...DEFAULT_FONT_SOURCES };
let configuredFontVfs: Record<string, string> | null = null;
let configuredVfsFileNames: FontSourceMap = {
  normal: "CiscoSans-Regular.otf",
  bold: "CiscoSans-Bold.otf",
  italics: "CiscoSans-Italic.otf",
  bolditalics: "CiscoSans-BoldItalic.otf",
};
let fontSetupPromise: Promise<void> | null = null;
let fontSetupGeneration = 0;
const fontBinaryCache = new Map<string, { name: string; base64: string }>();
const emojiImageDataUriCache = new Map<string, string>();

// Configure fonts before export. Supports:
// 1) source URLs for TTF fetching
// 2) prebuilt VFS object injection (recommended for stable production setup)
export function configureMarkdownPdfFonts(options?: MarkdownPdfFontOptions): void {
  configuredFontFamily = options?.fontFamily || DEFAULT_FONT_FAMILY;
  configuredFontSources = {
    ...DEFAULT_FONT_SOURCES,
    ...(options?.sources || {}),
  };
  configuredVfsFileNames = {
    normal: "CiscoSans-Regular.otf",
    bold: "CiscoSans-Bold.otf",
    italics: "CiscoSans-Italic.otf",
    bolditalics: "CiscoSans-BoldItalic.otf",
    ...(options?.vfsFileNames || {}),
  };
  configuredFontVfs = options?.vfs || null;
  fontSetupPromise = null;
  fontsReady = false;
  fontSetupGeneration++;
  cachedDocFontFamily = null;
}

const MARKDOWN_CSS = `
.markdown-pdf h1, .markdown-pdf h2, .markdown-pdf h3, .markdown-pdf h4, .markdown-pdf h5, .markdown-pdf h6 {
  font-weight: 700;
  margin: 16px 0 10px;
}
.markdown-pdf strong, .markdown-pdf b {
  font-weight: 700;
}
.markdown-pdf em, .markdown-pdf i {
  font-style: italic;
}
.markdown-pdf p, .markdown-pdf ul, .markdown-pdf ol, .markdown-pdf pre, .markdown-pdf table, .markdown-pdf blockquote {
  margin: 0 0 2px;
}
.markdown-pdf table {
  width: 100%;
  border-collapse: collapse;
}
.markdown-pdf th, .markdown-pdf td {
  border: 1px solid #d0d7de;
  padding: 6px 8px;
}
.markdown-pdf code {
  background: #f6f8fa;
  padding: 2px 4px;
  border-radius: 3px;
}
.markdown-pdf pre {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 10px;
}
.markdown-pdf img.emoji {
  display: inline-block;
  vertical-align: -0.22em;
  margin: 0;
  padding: 0;
  border: 0;
}
`;

function sanitizeFileName(fileName?: string): string {
  const normalized = (fileName || DEFAULT_FILE_NAME).trim();
  const withExtension = normalized.toLowerCase().endsWith(".pdf")
    ? normalized
    : `${normalized}.pdf`;
  return withExtension.replace(/[\\/:*?"<>|]/g, "_");
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunks: string[] = [];

  for (let i = 0; i < bytes.length; i += 0x8000) {
    const chunk = bytes.subarray(i, Math.min(i + 0x8000, bytes.length));
    chunks.push(String.fromCharCode.apply(null, chunk as unknown as number[]));
  }

  return btoa(chunks.join(""));
}

async function loadImageAsDataUri(url: string): Promise<string> {
  const cached = emojiImageDataUriCache.get(url);
  if (cached) {
    return cached;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load emoji image from ${url} (status: ${response.status})`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const contentType = response.headers.get("content-type") || "image/png";
  const dataUri = `data:${contentType};base64,${toBase64(arrayBuffer)}`;

  emojiImageDataUriCache.set(url, dataUri);
  return dataUri;
}

function renderTwemojiHtml(html: string): string {
  return twemoji.parse(html, {
    base: TWEMOJI_CDN_BASE_URL,
    folder: TWEMOJI_SVG_FOLDER,
    ext: ".svg",
    className: "emoji",
    attributes: () => ({
      draggable: "false",
      width: "12",
      height: "12",
      style: "display:inline-block;vertical-align:-0.22em;margin:0;padding:0;border:0;",
    }),
  });
}

async function loadSvgAsPngDataUri(url: string): Promise<string> {
  const cacheKey = `${url}#png`;
  const cached = emojiImageDataUriCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load emoji SVG from ${url} (status: ${response.status})`);
  }

  const svgMarkup = await response.text();
  const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml" });
  const objectUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to decode SVG image: ${url}`));
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = EMOJI_RASTER_SIZE;
    canvas.height = EMOJI_RASTER_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to create 2D canvas context for emoji rasterization.");
    }
    ctx.drawImage(image, 0, 0, EMOJI_RASTER_SIZE, EMOJI_RASTER_SIZE);

    const dataUri = canvas.toDataURL("image/png");
    emojiImageDataUriCache.set(cacheKey, dataUri);
    return dataUri;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function getEmojiCandidateUrls(src: string): string[] {
  if (!src || src.startsWith("data:")) {
    return [];
  }

  const rawName = src.split("?")[0].split("#")[0].split("/").pop();
  if (!rawName) {
    return [];
  }

  const svgName = rawName.replace(/\.png$/i, ".svg");
  const pngName = rawName.replace(/\.svg$/i, ".png");

  return Array.from(
    new Set([
      src,
      `${TWEMOJI_CDN_BASE_URL}${TWEMOJI_SVG_FOLDER}/${svgName}`,
      `${TWEMOJI_UNPKG_BASE_URL}${TWEMOJI_SVG_FOLDER}/${svgName}`,
      `${TWEMOJI_CDN_BASE_URL}${TWEMOJI_PNG_FOLDER}/${pngName}`,
      `${TWEMOJI_UNPKG_BASE_URL}${TWEMOJI_PNG_FOLDER}/${pngName}`,
    ])
  );
}

async function loadEmojiDataUriWithFallback(src: string): Promise<string> {
  const candidates = getEmojiCandidateUrls(src);
  if (!candidates.length) {
    return loadImageAsDataUri(src);
  }

  let lastError: unknown = null;
  for (const candidate of candidates) {
    try {
      if (candidate.toLowerCase().endsWith(".svg")) {
        return await loadSvgAsPngDataUri(candidate);
      }
      return await loadImageAsDataUri(candidate);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Failed to load emoji image from all fallback sources.");
}

async function inlineTwemojiImages(html: string): Promise<string> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const emojiNodes = Array.from(doc.querySelectorAll("img.emoji[src]"));

  if (!emojiNodes.length) {
    return html;
  }

  const uniqueSrc = Array.from(
    new Set(emojiNodes.map((node) => node.getAttribute("src") || "").filter((src) => !!src))
  );

  const dataUriMap = new Map<string, string>();
  await Promise.all(
    uniqueSrc.map(async (src) => {
      try {
        dataUriMap.set(src, await loadEmojiDataUriWithFallback(src));
      } catch {
        console.warn("[exportMarkdownToPDF] Failed to inline emoji image:", src);
      }
    })
  );

  emojiNodes.forEach((node) => {
    const src = node.getAttribute("src") || "";
    const inlinedSrc = dataUriMap.get(src);
    node.removeAttribute("alt");
    node.removeAttribute("title");
    node.setAttribute(
      "style",
      "display:inline-block;vertical-align:-0.22em;margin:0;padding:0;border:0;"
    );
    if (inlinedSrc) {
      node.setAttribute("src", inlinedSrc);
    }
  });

  return doc.body.innerHTML;
}

function getFileNameFromUrl(url: string): string {
  const fallback = "font.ttf";
  const cleanUrl = url.split("?")[0].split("#")[0];
  const fileName = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
  return fileName || fallback;
}

async function loadFontToVfs(url: string): Promise<{ name: string; base64: string }> {
  const cached = fontBinaryCache.get(url);
  if (cached) {
    return cached;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load font from ${url} (status: ${response.status})`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const loaded = {
    name: getFileNameFromUrl(url),
    base64: toBase64(arrayBuffer),
  };

  fontBinaryCache.set(url, loaded);
  return loaded;
}

let pdfMakeInstancePromise: Promise<PdfMakeLike> | null = null;
let markedModulePromise: Promise<MarkedModule> | null = null;
let htmlToPdfMakePromise: Promise<HtmlToPdfMakeFn> | null = null;
let bundledPdfFontsPromise: Promise<void> | null = null;

// Synchronous caches — populated after first resolution, eliminating await overhead on repeat calls.
let cachedPdfMake: PdfMakeLike | null = null;
let cachedMarked: MarkedModule | null = null;
let cachedHtmlToPdfMake: HtmlToPdfMakeFn | null = null;

function extractBundledVfs(mod: unknown): Record<string, string> | null {
  if (!mod || typeof mod !== "object") {
    return null;
  }

  const typedModule = mod as Record<string, unknown> & {
    default?: Record<string, unknown>;
  };
  const defaultExport =
    typedModule.default && typeof typedModule.default === "object"
      ? (typedModule.default as Record<string, unknown>)
      : null;
  const modulePdfMake =
    typedModule.pdfMake && typeof typedModule.pdfMake === "object"
      ? (typedModule.pdfMake as { vfs?: Record<string, string> })
      : null;
  const defaultPdfMake =
    defaultExport?.pdfMake && typeof defaultExport.pdfMake === "object"
      ? (defaultExport.pdfMake as { vfs?: Record<string, string> })
      : null;
  const defaultVfs =
    defaultExport?.vfs && typeof defaultExport.vfs === "object"
      ? (defaultExport.vfs as Record<string, string>)
      : null;
  const moduleVfs =
    typedModule.vfs && typeof typedModule.vfs === "object"
      ? (typedModule.vfs as Record<string, string>)
      : null;

  return defaultPdfMake?.vfs || modulePdfMake?.vfs || defaultVfs || moduleVfs;
}

function getBundledPdfFontsPromise(pdfMake: PdfMakeLike): Promise<void> {
  if (!bundledPdfFontsPromise) {
    bundledPdfFontsPromise = import("pdfmake/build/vfs_fonts")
      .then((mod) => {
        const bundledVfs = extractBundledVfs(mod);
        if (!bundledVfs) {
          throw new Error("Failed to resolve bundled pdfmake VFS fonts.");
        }

        pdfMake.vfs = {
          ...bundledVfs,
          ...(pdfMake.vfs || {}),
        };
      })
      .catch((error) => {
        bundledPdfFontsPromise = null;
        throw error;
      });
  }

  return bundledPdfFontsPromise;
}

function getPdfMakeInstance(): Promise<PdfMakeLike> {
  if (!pdfMakeInstancePromise) {
    pdfMakeInstancePromise = import("pdfmake/build/pdfmake").then((mod) => {
      cachedPdfMake = (mod.default || mod) as PdfMakeLike;
      return cachedPdfMake;
    });
  }
  return pdfMakeInstancePromise;
}

function getMarkedModule(): Promise<MarkedModule> {
  if (!markedModulePromise) {
    markedModulePromise = (import("marked") as Promise<MarkedModule>).then((mod) => {
      cachedMarked = mod;
      return mod;
    });
  }
  return markedModulePromise;
}

function getHtmlToPdfMake(): Promise<HtmlToPdfMakeFn> {
  if (!htmlToPdfMakePromise) {
    htmlToPdfMakePromise = import("html-to-pdfmake").then((mod) => {
      cachedHtmlToPdfMake = (mod.default || mod) as HtmlToPdfMakeFn;
      return cachedHtmlToPdfMake;
    });
  }
  return htmlToPdfMakePromise;
}

function normalizeAccidentalRepeat(text: string): string {
  const raw = text.trim();
  if (!raw || raw.length < 8) {
    return text;
  }

  // Case 1: exact duplication (e.g. "MeetingsMeetings").
  if (raw.length % 2 === 0) {
    const half = raw.length / 2;
    if (raw.slice(0, half) === raw.slice(half)) {
      return raw.slice(0, half);
    }
  }

  // Case 2: prefix + duplicated tail (e.g. "totalVideoMeetingsVideoMeetings").
  for (let i = 1; i < raw.length - 1; i++) {
    const tail = raw.slice(i);
    if (tail.length % 2 !== 0) {
      continue;
    }
    const half = tail.length / 2;
    const left = tail.slice(0, half);
    const right = tail.slice(half);
    if (left && left === right) {
      return `${raw.slice(0, i)}${left}`;
    }
  }

  return text;
}

function normalizeHeaderCellText(cell: unknown): void {
  if (!cell || typeof cell !== "object") {
    return;
  }

  if (Array.isArray(cell)) {
    cell.forEach(normalizeHeaderCellText);
    return;
  }

  const typed = cell as Record<string, unknown>;
  if (typeof typed.text === "string") {
    typed.text = normalizeAccidentalRepeat(typed.text);
  } else if (typed.text && typeof typed.text === "object") {
    normalizeHeaderCellText(typed.text);
  }

  for (const value of Object.values(typed)) {
    if (value && typeof value === "object" && value !== typed.text) {
      normalizeHeaderCellText(value);
    }
  }
}

function applyTableLayout(node: unknown): void {
  if (Array.isArray(node)) {
    node.forEach(applyTableLayout);
    return;
  }

  if (!node || typeof node !== "object") {
    return;
  }

  const typedNode = node as Record<string, unknown>;

  if (typedNode.table) {
    const table = typedNode.table as Record<string, unknown>;
    const body = Array.isArray(table.body) ? (table.body as unknown[]) : [];
    let columnCount = 0;

    for (const row of body) {
      if (Array.isArray(row)) {
        columnCount = Math.max(columnCount, row.length);
      }
    }

    if (columnCount > 0) {
      table.widths = Array.from({ length: columnCount }, () => "*");
    }

    // Only normalize header row text to avoid changing body data.
    const headerRow = body[0];
    if (Array.isArray(headerRow)) {
      headerRow.forEach(normalizeHeaderCellText);
    }

    // html-to-pdfmake (tableAutoSize mode) writes the DOM-measured pixel width
    // onto the wrapper node itself. Delete it so pdfmake respects "*" column widths.
    delete typedNode.width;

    typedNode.layout = {
      hLineWidth: () => 0.8,
      vLineWidth: () => 0.8,
      hLineColor: () => "#d0d7de",
      vLineColor: () => "#d0d7de",
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 4,
      paddingBottom: () => 4,
    };
  }

  Object.values(typedNode).forEach((value) => {
    if (value && typeof value === "object") {
      applyTableLayout(value);
    }
  });
}

function isEmojiImageNode(node: unknown): node is Record<string, unknown> {
  if (!node || typeof node !== "object" || Array.isArray(node)) {
    return false;
  }

  const typedNode = node as Record<string, unknown>;
  return (
    typedNode.nodeName === "IMG" &&
    typeof typedNode.image === "string" &&
    String(typedNode.image).startsWith("data:image/")
  );
}

function toColumnContent(node: unknown): Record<string, unknown> {
  if (node && typeof node === "object" && !Array.isArray(node)) {
    return node as Record<string, unknown>;
  }

  return {
    text: typeof node === "string" ? node : "",
  };
}

function fixEmojiInlineWrapping(node: unknown): unknown {
  if (Array.isArray(node)) {
    return node.map(fixEmojiInlineWrapping);
  }

  if (!node || typeof node !== "object") {
    return node;
  }

  const typedNode = node as Record<string, unknown>;

  Object.entries(typedNode).forEach(([key, value]) => {
    if (value && typeof value === "object") {
      typedNode[key] = fixEmojiInlineWrapping(value);
    }
  });

  const stack = Array.isArray(typedNode.stack) ? (typedNode.stack as unknown[]) : null;

  if (!stack || stack.length < 2 || !isEmojiImageNode(stack[0])) {
    return typedNode;
  }

  const [, ...restChildren] = stack;
  if (!restChildren.length) {
    return typedNode;
  }

  const textColumn =
    restChildren.length === 1 ? toColumnContent(restChildren[0]) : { stack: restChildren };

  const imageNode = stack[0] as Record<string, unknown>;
  const { stack: _stack, nodeName: _nodeName, ...restProps } = typedNode;

  return {
    ...restProps,
    columns: [
      {
        ...imageNode,
        width: typeof imageNode.width === "number" ? imageNode.width : undefined,
        margin: [0, 1, 0, 0],
      },
      {
        ...textColumn,
        width: "*",
      },
    ],
    columnGap: 2,
  };
}

let fontsReady = false;

async function ensureLightweightPdfFonts(pdfMake: PdfMakeLike): Promise<void> {
  await getBundledPdfFontsPromise(pdfMake);

  pdfMake.fonts = {
    ...(pdfMake.fonts || {}),
    [LIGHTWEIGHT_FONT_FAMILY]: {
      normal: "Roboto-Regular.ttf",
      bold: "Roboto-Medium.ttf",
      italics: "Roboto-Italic.ttf",
      bolditalics: "Roboto-MediumItalic.ttf",
    },
  };
}

function requiresEmbeddedFonts(text: string): boolean {
  return NON_STANDARD_PDF_GLYPH_REGEX.test(text);
}

async function ensurePdfMakeFonts(pdfMake: PdfMakeLike): Promise<void> {
  // Synchronous fast path — skip all async overhead on repeat calls.
  if (fontsReady) {
    return;
  }

  if (fontSetupPromise) {
    await fontSetupPromise;
    return;
  }

  fontSetupPromise = (async () => {
    const generation = fontSetupGeneration;
    const vfs = pdfMake.vfs || {};

    if (configuredFontVfs) {
      Object.assign(vfs, configuredFontVfs);
    } else {
      const [normal, bold, italics, bolditalics] = await Promise.all([
        loadFontToVfs(configuredFontSources.normal),
        loadFontToVfs(configuredFontSources.bold),
        loadFontToVfs(configuredFontSources.italics),
        loadFontToVfs(configuredFontSources.bolditalics),
      ]);
      const resolvedFonts = { normal, bold, italics, bolditalics };

      for (const item of Object.values(resolvedFonts)) {
        vfs[item.name] = item.base64;
      }

      configuredVfsFileNames = {
        normal: resolvedFonts.normal.name,
        bold: resolvedFonts.bold.name,
        italics: resolvedFonts.italics.name,
        bolditalics: resolvedFonts.bolditalics.name,
      };

      // Font binaries are now in VFS — release the duplicate base64 cache (~2-5MB).
      fontBinaryCache.clear();
    }

    pdfMake.vfs = vfs;
    pdfMake.fonts = {
      ...(pdfMake.fonts || {}),
      [configuredFontFamily]: {
        normal: configuredVfsFileNames.normal,
        bold: configuredVfsFileNames.bold,
        italics: configuredVfsFileNames.italics,
        bolditalics: configuredVfsFileNames.bolditalics,
      },
    };

    const fontDef = pdfMake.fonts?.[configuredFontFamily];
    if (!fontDef || !vfs[fontDef.normal]) {
      throw new Error(`Font is not ready in VFS for family '${configuredFontFamily}'.`);
    }
    // Only mark ready if config hasn't changed during async setup.
    if (generation === fontSetupGeneration) {
      fontsReady = true;
    }
  })().catch((error) => {
    // Allow retries after a failed initialization.
    fontSetupPromise = null;
    fontsReady = false;
    throw error;
  });

  await fontSetupPromise;
}

let cachedDocFontFamily: string | null = null;

function hasDistinctStyleFonts(pdfMake: PdfMakeLike, family: string): boolean {
  const fontDef = pdfMake.fonts?.[family];
  const vfs = pdfMake.vfs || {};
  if (!fontDef) {
    return false;
  }

  const regular = vfs[fontDef.normal];
  const bold = vfs[fontDef.bold];
  const italics = vfs[fontDef.italics];
  const bolditalics = vfs[fontDef.bolditalics];

  if (!regular || !bold || !italics || !bolditalics) {
    return false;
  }

  // Compare lengths first (fast) — if all same length, compare a prefix to avoid
  // creating a Set over multi-MB base64 strings on every export call.
  const lengths = [regular.length, bold.length, italics.length, bolditalics.length];
  const uniqueLengths = new Set(lengths);
  if (uniqueLengths.size > 1) {
    return true;
  }
  // Same length — compare a short prefix to distinguish identical files.
  const prefix = 64;
  const samples = new Set([
    regular.slice(0, prefix),
    bold.slice(0, prefix),
    italics.slice(0, prefix),
    bolditalics.slice(0, prefix),
  ]);
  return samples.size > 1;
}

function resolveDocumentFontFamily(pdfMake: PdfMakeLike): string {
  if (cachedDocFontFamily) {
    return cachedDocFontFamily;
  }

  if (hasDistinctStyleFonts(pdfMake, configuredFontFamily)) {
    cachedDocFontFamily = configuredFontFamily;
    return cachedDocFontFamily;
  }

  if (hasDistinctStyleFonts(pdfMake, FALLBACK_FONT_FAMILY)) {
    console.warn(
      `[exportMarkdownToPDF] Font family '${configuredFontFamily}' does not provide distinct bold/italic binaries. Falling back to '${FALLBACK_FONT_FAMILY}' for correct emphasis rendering.`
    );
    cachedDocFontFamily = FALLBACK_FONT_FAMILY;
    return cachedDocFontFamily;
  }

  cachedDocFontFamily = configuredFontFamily;
  return cachedDocFontFamily;
}

export async function exportMarkdownToPDF(mdString: string, fileName?: string): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("exportMarkdownToPDF can only run in the browser.");
  }

  if (!mdString || !mdString.trim()) {
    throw new Error("Markdown content is empty.");
  }

  console.log("[exportMarkdownToPDF] === Original Markdown ===\n", mdString);

  try {
    // Use synchronous caches when available (repeat exports), fall back to async on first call.
    const pdfMake = cachedPdfMake || (await getPdfMakeInstance());
    const shouldEmbedFonts = requiresEmbeddedFonts(mdString);

    let docFontFamily = LIGHTWEIGHT_FONT_FAMILY;
    if (shouldEmbedFonts) {
      await ensurePdfMakeFonts(pdfMake);
      docFontFamily = resolveDocumentFontFamily(pdfMake);
    } else {
      await ensureLightweightPdfFonts(pdfMake);
    }

    if (!cachedMarked || !cachedHtmlToPdfMake) {
      await Promise.all([getMarkedModule(), getHtmlToPdfMake()]);
    }
    const { marked } = cachedMarked!;
    const htmlToPdfmake = cachedHtmlToPdfMake!;

    const parsedHtml = marked.parse(mdString, {
      gfm: true,
      breaks: true,
    }) as string;

    const emojiHtml = renderTwemojiHtml(parsedHtml);
    const inlinedEmojiHtml = await inlineTwemojiImages(emojiHtml);

    const html = `<style>${MARKDOWN_CSS}</style><div class="markdown-pdf">${inlinedEmojiHtml}</div>`;

    const pdfContent = htmlToPdfmake(html, {
      defaultStyles: {
        h1: { fontSize: 15, bold: true, margin: [0, 9, 0, 7] },
        h2: { fontSize: 14, bold: true, margin: [0, 9, 0, 6] },
        h3: { fontSize: 13, bold: true, margin: [0, 8, 0, 6] },
        h4: { fontSize: 12, bold: true, margin: [0, 7, 0, 5] },
        h5: { fontSize: 11, bold: true, margin: [0, 7, 0, 5] },
        h6: { fontSize: 10, bold: true, margin: [0, 6, 0, 4] },
        p: { margin: [0, 0, 0, 0], padding: [0, 0, 0, 0] },
        strong: { bold: true },
        b: { bold: true },
        em: { italics: true },
        i: { italics: true },
        a: { color: "#0969da", decoration: "underline" },
        blockquote: {
          margin: [0, 8, 0, 8],
          border: [3, 0, 0, 0],
          borderColor: ["#d0d7de", "#d0d7de", "#d0d7de", "#d0d7de"],
          padding: [8, 4, 8, 4],
          color: "#57606a",
        },
        code: {
          fontSize: 8,
          background: "#f6f8fa",
        },
        pre: {
          margin: [0, 8, 0, 12],
          background: "#f6f8fa",
        },
        table: { margin: [0, 0, 0, 0] },
        th: { bold: true, fillColor: "#f6f8fa" },
        td: {},
      },
      tableAutoSize: true,
      window,
    }) as unknown;

    const normalizedPdfContent = fixEmojiInlineWrapping(pdfContent);
    applyTableLayout(normalizedPdfContent);

    const safeFileName = sanitizeFileName(fileName);
    const documentDefinition: Record<string, unknown> = {
      info: {
        title: safeFileName,
      },
      pageSize: "A4",
      pageMargins: [36, 40, 36, 40],
      defaultStyle: {
        font: docFontFamily,
        fontSize: 9,
        lineHeight: 1.24,
      },
      content: normalizedPdfContent,
    };

    await new Promise<void>((resolve, reject) => {
      try {
        pdfMake.createPdf(documentDefinition).download(safeFileName, () => {
          resolve();
        });
      } catch (error) {
        reject(error);
      }
    });
  } catch (error) {
    throw error;
  }
}

/**
 * Eagerly load pdfmake module, font files, marked, and html-to-pdfmake in parallel.
 * Call on component mount to eliminate cold-start delay on first export.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function preloadPdfResources(): void {
  Promise.all([
    getPdfMakeInstance().then((pdfMake) => ensureLightweightPdfFonts(pdfMake)),
    getMarkedModule(),
    getHtmlToPdfMake(),
  ]).catch(() => {
    // Silently ignore preload failures; the actual export will retry and surface errors.
  });
}

export function preloadPdfResourcesForContent(mdString: string): void {
  preloadPdfResources();

  if (!mdString || !mdString.trim() || !requiresEmbeddedFonts(mdString)) {
    return;
  }

  getPdfMakeInstance()
    .then((pdfMake) => ensurePdfMakeFonts(pdfMake))
    .catch(() => {
      // Silently ignore preload failures; the actual export will retry and surface errors.
    });
}

export class PdfGenerator {
  private readonly options: Required<PdfGeneratorOptions>;

  constructor(options?: PdfGeneratorOptions) {
    this.options = {
      fileName: options?.fileName || DEFAULT_FILE_NAME,
    };
  }

  async export(mdString: string): Promise<void> {
    await exportMarkdownToPDF(mdString, this.options.fileName);
  }
}

export async function demoExportMarkdownToProfessionalPdf(markdown: string): Promise<void> {
  await exportMarkdownToPDF(markdown, "markdown-professional-demo.pdf");
}

export default PdfGenerator;
